/**
 * Human-readable copy for the backend's `ErrorCode` union (backend
 * src/middleware/errorHandler.ts). The API answers with { code, message } where `message` is
 * written for a developer; showing the raw SCREAMING_SNAKE_CASE code as a toast headline made
 * every failure look like a crash to counter staff. This module is the single translation
 * point — `showApiError` in components/ui/Toast.tsx runs everything through it.
 *
 * The WORDING lives in the i18n catalogue (src/i18n/locales/{fr,en}/errors.json), keyed by the
 * error code. What stays here is the BEHAVIOUR that is not language-dependent: which codes defer
 * to the server's own message, and how a response with no recognised code falls back by HTTP
 * status. Adding a code therefore means adding it to both JSON files — and to `USE_SERVER_MESSAGE`
 * below only if the server text is the part that makes it actionable.
 *
 * Copy is read off the i18next instance rather than a hook because these functions are called
 * from promise callbacks and interceptors, where there is no component to re-render.
 */
import i18n from '../i18n';

export interface ErrorCopy {
  /** Short, plain-language headline shown in bold. */
  title: string;
  /** What happened and what the operator should do about it. */
  message: string;
}

/**
 * Codes where the server's own `message` is shown as the body instead of the catalogue copy —
 * used where the server text carries the specifics that make it actionable (which field failed
 * validation, which amount is short, which setting is unknown, which toxic pair was refused).
 */
const USE_SERVER_MESSAGE = new Set([
  'AMOUNT_MISMATCH',
  'AMOUNT_BELOW_MINIMUM',
  'PAYMENT_REQUIRED',
  'SHIPMENT_NOT_PICKED',
  'SHIPMENT_QUANTITY_INVALID',
  'ORDER_NOT_MODIFIABLE',
  'DISCOUNT_REASON_TOO_SHORT',
  'DISCOUNT_EXCEEDS_LIMIT',
  // WP-9 — the server names the offending pair and explains why it is refused; that text is far
  // more useful than any generic copy, so it is shown as-is under our headline.
  'TOXIC_COMBINATION',
  'DEPENDENCY_UNMET',
  'QUANTITY_EXCEEDS_ORDERED',
  'WEAK_PASSWORD',
  'CREDIT_NOTE_EXCEEDS_INVOICE',
  'OVERPAYMENT',
  'EMAIL_SEND_FAILED',
  'VALIDATION_ERROR',
  'IMPORT_TOO_LARGE',
  'FEATURE_NOT_AVAILABLE',
  // Nothing in the platform is broken — its separate catalogue server isn't answering. The
  // server message names the host and driver code, which is what an operator needs.
  'TECDOC_UNAVAILABLE',
  // 2026-10-30 — the server names the line / the supplier order / the reserved quantities.
  'SUPPLIER_SHORT',
  'SUPPLIER_NO_ANSWER',
  'SUPPLIER_BLOCKED',
  'SUPPLIER_IN_USE',
]);

/** HTTP statuses whose fallback copy is an alias of a code rather than status-specific copy. */
const STATUS_ALIASES: Record<number, string> = {
  401: 'NOT_AUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  500: 'INTERNAL_ERROR',
};

/** Statuses that carry their own copy in errors.json under `status.*`. */
const STATUS_WITH_COPY = new Set([400, 408, 422, 429, 502, 503]);

/** Statuses whose fallback copy defers to the server message, for the same reason as above. */
const STATUS_USE_SERVER_MESSAGE = new Set([400, 422, 503]);

/** Look a catalogue entry up, returning null when the key is not in the catalogue at all. */
function lookup(prefix: string): ErrorCopy | null {
  const title = i18n.t(`${prefix}.title`, { defaultValue: '' });
  if (!title) return null;
  return { title, message: i18n.t(`${prefix}.message`, { defaultValue: '' }) };
}

/**
 * Resolve an API failure to display copy. Falls back through: known code → HTTP status →
 * generic. The server's message is only surfaced when it adds actionable specifics, so a raw
 * technical string is never the headline.
 */
export function describeApiError(
  code: string | undefined,
  serverMessage: string | undefined,
  status?: number
): ErrorCopy {
  const byCode = code ? lookup(`errors.codes.${code}`) : null;
  const aliased = !byCode && status ? STATUS_ALIASES[status] : undefined;
  const copy =
    byCode ??
    (aliased ? lookup(`errors.codes.${aliased}`) : null) ??
    (status && STATUS_WITH_COPY.has(status) ? lookup(`errors.status.${status}`) : null);

  if (!copy) {
    return {
      title: i18n.t('errors.fallback.title'),
      message: serverMessage?.trim() || i18n.t('errors.fallback.message'),
    };
  }

  const deferToServer = byCode
    ? USE_SERVER_MESSAGE.has(code!)
    : !!status && STATUS_USE_SERVER_MESSAGE.has(status);
  const body = deferToServer && serverMessage?.trim() ? serverMessage.trim() : copy.message;
  return { title: copy.title, message: body };
}

/** Friendly headline for a code, used where only a title is needed. */
export function errorTitle(code: string | undefined, status?: number): string {
  return describeApiError(code, undefined, status).title;
}
